import { FC, useCallback, useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Table } from 'core-components/library/list'
import type { ICockpitExportView } from 'partner360/features/financialCockpit/api'
import {
  formatCockpitMoney,
  getPlanQualityHierarchyRows,
  TRevenueAndSobHierarchyOrder,
} from 'partner360/features/financialCockpit/lib'
import { useFinSobHierarchyBreakdown } from 'partner360/features/financialCockpit/model/hooks'
import { useCockpitSettings } from 'partner360/features/financialCockpit/model/hooks/useCockpitSettings'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from 'partner360/features/financialCockpit/ui/components/cockpitExport'
import { CockpitHierarchyOrder } from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyOrder'
import {
  getHierarchyExportTable,
  getPopulatedHierarchyRows,
  useCockpitPartnerLinkParams,
  useGeoShortNameById,
  useHierarchyExpansion,
  useOfferingNameByCode,
  usePartnerNameById,
} from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyTable'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'

import { getPlanQualityTableColumns } from './helpers/getPlanQualityTableColumns'

import styles from './CockpitPlanQualityTable.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob

/** Names the hierarchy-order switch in the export, as the other cockpit tables do. */
const HIERARCHY_LABEL = 'Hierarchy'

interface ICockpitPlanQualityTableProps {
  /** Whether the Geo axis (and its Geo>Product/Product>Geo switcher) is shown at all — manager
   *  cockpit mode only, exactly as in `CockpitRevenueAndSobTable`. A single partner has one geo,
   *  so a geo breakdown isn't meaningful in partner mode. */
  showGeoHierarchy: boolean
  exportSlot?: TCockpitExportSlot
}

export const CockpitPlanQualityTable: FC<ICockpitPlanQualityTableProps> = ({
  showGeoHierarchy,
  exportSlot,
}) => {
  const [userHierarchyOrder, setHierarchyOrder] =
    useState<TRevenueAndSobHierarchyOrder>('geoProduct')
  /** Pinning to `'productGeo'` when the Geo axis is disabled makes every (product-only) edge
   *  classify as primary in `useHierarchyExpansion`, since no row is ever Geo-shaped in that case. */
  const hierarchyOrder = showGeoHierarchy ? userHierarchyOrder : 'productGeo'

  const { selectedFYear: fYear, currency, showQuota } = useCockpitSettings()
  const { rows: allHierarchyRows } = useFinSobHierarchyBreakdown(showGeoHierarchy, hierarchyOrder)

  /** The same prune the Revenue & SOB table applies, run on the figures before they become scores. */
  const hierarchyRows = useMemo(
    () => getPopulatedHierarchyRows(allHierarchyRows, showQuota),
    [allHierarchyRows, showQuota]
  )

  const qualityRows = useMemo(() => getPlanQualityHierarchyRows(hierarchyRows), [hierarchyRows])

  const geoShortNameById = useGeoShortNameById()
  const offeringNameByCode = useOfferingNameByCode()
  const partnerNameById = usePartnerNameById()
  const partnerLinkParams = useCockpitPartnerLinkParams()
  const { visibleRows, controls } = useHierarchyExpansion(qualityRows, hierarchyOrder)
  const translate = useSafetyTranslation()

  const formatMoney = useCallback(
    (value: number | null) => formatCockpitMoney(value, currency),
    [currency]
  )

  const columns = useMemo(
    () =>
      getPlanQualityTableColumns({
        fYear,
        formatMoney,
        showQuota,
        showGeoHierarchy,
        hierarchyOrder,
        controls,
        geoShortNameById,
        offeringNameByCode,
        partnerNameById,
        partnerLinkParams,
        translate,
      }),
    [
      fYear,
      formatMoney,
      showQuota,
      showGeoHierarchy,
      hierarchyOrder,
      controls,
      geoShortNameById,
      offeringNameByCode,
      partnerNameById,
      partnerLinkParams,
      translate,
    ]
  )

  /** The table as it stands, read off the very columns that draw it — the same snapshot the
   *  Revenue & SOB table builds, on this table's own columns. */
  const exportView = useCallback(
    (): ICockpitExportView => ({
      key: 'finSobQuality',
      controls: showGeoHierarchy
        ? [
            {
              label: HIERARCHY_LABEL,
              value: translate(
                hierarchyOrder === 'geoProduct'
                  ? KEYS.columnOrderGeoProduct
                  : KEYS.columnOrderProductGeo
              ),
              options: [
                translate(KEYS.columnOrderProductGeo),
                translate(KEYS.columnOrderGeoProduct),
              ],
            },
          ]
        : [],
      table: getHierarchyExportTable({ columns, rows: visibleRows, controls, translate }),
    }),
    [showGeoHierarchy, hierarchyOrder, columns, visibleRows, controls, translate]
  )

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <div className={styles.root}>
      {showGeoHierarchy && (
        <CockpitHierarchyOrder selectedOrder={hierarchyOrder} onSelectOrder={setHierarchyOrder} />
      )}

      <Table
        items={visibleRows}
        classes={{ body: styles.body }}
        columns={columns}
        keyField="rowId"
        pageSize={visibleRows.length}
        header
        className={styles.table}
        placeholderContent={() => <Translation text={KEYS.noRowsAtLevel} span />}
      />
    </div>
  )
}

CockpitPlanQualityTable.displayName = 'CockpitPlanQualityTable'
